import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Save from '@mui/icons-material/Save';
import Lock from '@mui/icons-material/Lock';
import Edit from '@mui/icons-material/Edit';
import Close from '@mui/icons-material/Close';
import {
  platformSettingsService,
  GroupedSettings,
  PlatformSettingDto,
  StartupHealthDto,
} from '../../services/platformSettingsService';
import { apiErrorMessage } from '../../utils/apiUtils';

const CATEGORY_BASE_LABELS: Record<string, string> = {
  AIAnalysis: 'AI Analysis',
  AISummary: 'AI Summary',
  AISecondary: 'Secondary AI Endpoint (Failover)',
  Transcription: 'Transcription (Azure Speech)',
  Processing: 'Processing',
  Retry: 'Retry Policy',
  Retention: 'Data Retention',
  Upload: 'Upload',
  Audit: 'Audit Trail',
  Notifications: 'Notifications (Scheduled Email)',
};

/** Fixed accordion order: the two model groups first, then the shared failover endpoint. */
const CATEGORY_ORDER = [
  'AIAnalysis',
  'AISummary',
  'AISecondary',
  'Processing',
  'Retry',
  'Transcription',
  'Retention',
  'Upload',
  'Audit',
  'Notifications',
];

type Subsection = 'shared' | 'primary' | 'secondary';

const SUBSECTION_LABELS: Record<Subsection, string> = {
  shared: 'Shared parameters — apply to primary AND secondary',
  primary: 'Primary deployment (data zone)',
  secondary: 'Secondary deployment (failover)',
};

const SUBSECTION_ORDER: Subsection[] = ['shared', 'primary', 'secondary'];

/** Summary behavior params shared by primary + secondary (the rest of AISummary.* is primary-slot config). */
const SHARED_SUMMARY_KEYS = new Set([
  'AISummary.MaxTokens',
  'AISummary.ReasoningEffort',
  'AISummary.Verbosity',
]);

/** Categories whose rows render as Shared/Primary/Secondary subsections. */
const SUBSECTIONED_CATEGORIES = new Set(['AIAnalysis', 'AISummary']);

/**
 * Primary and secondary intentionally run the same model with the same behavior
 * parameters — only deployment names, endpoint, and rate budgets differ per slot.
 * AIModel.ParameterProfile is the PRIMARY analysis profile (the secondary has
 * AISecondary.AnalysisParameterProfile), so it renders under Primary, not Shared.
 * Only called for SUBSECTIONED_CATEGORIES.
 */
function getSubsection(category: string, key: string): Subsection {
  if (key.startsWith('AISecondary.')) return 'secondary';
  if (category === 'AIAnalysis') {
    if (key === 'AIModel.ParameterProfile') return 'primary';
    return key.startsWith('AIModel.') ? 'shared' : 'primary';
  }
  return SHARED_SUMMARY_KEYS.has(key) ? 'shared' : 'primary';
}

/** Build a display label for a category, appending the live primary/secondary deployment names. */
function getCategoryLabel(category: string, settings: GroupedSettings): string {
  const base = CATEGORY_BASE_LABELS[category] ?? category;
  if (category === 'AIAnalysis' || category === 'AISummary') {
    const rows = settings[category] ?? [];
    const find = (key: string) =>
      rows.find((s) => s.settingKey === key)?.settingValue?.trim();
    const primary = find(`${category}.DeploymentName`);
    const secondary = find(
      category === 'AIAnalysis'
        ? 'AISecondary.AnalysisDeploymentName'
        : 'AISecondary.SummaryDeploymentName'
    );
    if (primary && secondary) return `${base} (primary: ${primary} · secondary: ${secondary})`;
    if (primary) return `${base} (${primary})`;
  }
  return base;
}

const CATEGORY_INFO: Record<string, string> = {
  AIAnalysis:
    'Quality analysis model configuration. Shared parameters apply to BOTH the primary and secondary deployments so scoring stays consistent across failover — run the same model on both slots. Note: on gpt-5/o-series (reasoning profile) the sampling parameters (Temperature, TopP, penalties) are never sent — Azure rejects them; ReasoningEffort, Verbosity, and MaxTokens are the levers that apply. Per-slot rows control deployment name and rate budgets; set budgets to match each deployment’s own Azure AI Foundry quota.',
  AISummary:
    'Summarization model configuration. Independent from analysis — both run concurrently without competing. Shared parameters apply to BOTH the primary and secondary deployments; per-slot rows control deployment name and rate budgets (match each deployment’s own Azure AI Foundry quota).',
  AISecondary:
    'Optional second Azure OpenAI endpoint for failover and quota expansion. Endpoint must be an absolute https URL (e.g. https://ai-a3-secondary.openai.azure.com/) — model deployment names belong in the Secondary rows of AI Analysis / AI Summary. Set Endpoint to activate: the pool routes calls to the secondary deployments when the primary is throttled (Azure 429). Leave empty to run primary only. Changes take effect within 5 minutes. Prerequisite: grant the Managed Identity "Cognitive Services OpenAI User" role on the secondary Azure AI Services resource.',
  Transcription:
    'Azure Speech Batch API settings. Azure allows 100 API calls per 10 seconds across ALL operations (job creation, status checks, result downloads, deletes). Exceeding this returns HTTP 429 and the request is rejected (not queued). The platform enforces a sliding-window rate limiter on all Speech API calls — MaxApiRequestsPer10Seconds and SafetyMarginPercent control the effective budget. Batch processing is sequential per region — submitting faster does not speed up processing. Microsoft recommends: submit ~1000 files per request, poll status every 5-10 minutes, distribute requests over time.',
  Processing:
    'Controls how the AI analysis background service picks up transcribed calls. BatchSizePerTenant limits how many calls are fetched per tenant per polling cycle. Rate limiters control actual Azure OpenAI concurrency.',
  Retry:
    'When AI analysis fails (e.g., 429 from OpenAI, timeout, malformed response), calls move to ai_retry_pending status. This service retries them with exponential backoff. After max attempts, calls stay in failed status for manual review.',
  Retention:
    'Minimum retention period before a call recording may be deleted. Set to match state law requirements for production tenants. 0 = no floor enforced (demo/dev only).',
  Upload:
    'Controls the upload path for call recordings. DirectUploadEnabled must remain false until tenant storage Bicep is applied with CORS and firewall rules — while false the frontend routes uploads through the API relay.',
  Audit:
    'Audit trail retention and export limits. RetentionDays is a POLICY STATEMENT — nothing purges audit records automatically, and CJIS v6.0 AU-11 sets a floor of 365 days that lower values are refused against, not clamped to.',
  Notifications:
    'Scheduled outbound email. ScheduledDeliveryEnabled is a global kill switch across every agency: while it is false no digest is sent and no transport call is made, whatever a tenant has configured. It does NOT opt anyone in — each agency additionally enables its own digest under Settings → QA Digest, so turning this on sends only to agencies that have already asked for it.',
};

/** Split a category's rows into shared/primary/secondary subsections (AI categories only). */
function groupRows(
  category: string,
  rows: PlatformSettingDto[]
): { label: string | null; rows: PlatformSettingDto[] }[] {
  if (!SUBSECTIONED_CATEGORIES.has(category)) {
    return [{ label: null, rows }];
  }
  const buckets = new Map<Subsection, PlatformSettingDto[]>();
  for (const row of rows) {
    const sub = getSubsection(category, row.settingKey);
    const bucket = buckets.get(sub);
    if (bucket) bucket.push(row);
    else buckets.set(sub, [row]);
  }
  return SUBSECTION_ORDER.filter((sub) => buckets.has(sub)).map((sub) => ({
    label: SUBSECTION_LABELS[sub],
    rows: buckets.get(sub)!.sort((a, b) => a.settingKey.localeCompare(b.settingKey)),
  }));
}

export const PlatformSettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<GroupedSettings>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [startupHealth, setStartupHealth] = useState<StartupHealthDto | null>(null);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await platformSettingsService.getAll();
      setSettings(data);
      setError(null);
    } catch {
      setError('Failed to load platform settings');
    } finally {
      setLoading(false);
    }
  }, []);

  // Startup degradations are read separately and never block the page: a settings seed that
  // failed is precisely when an admin most needs to reach this screen. A failure to read the
  // health state is left silent for the same reason — it must not mask the settings themselves.
  const loadStartupHealth = useCallback(async () => {
    try {
      setStartupHealth(await platformSettingsService.getStartupHealth());
    } catch {
      setStartupHealth(null);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadStartupHealth();
  }, [loadSettings, loadStartupHealth]);

  const handleEdit = (setting: PlatformSettingDto) => {
    setEditingKey(setting.settingKey);
    setEditValue(setting.settingValue);
    setSuccess(null);
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const handleSave = async (key: string) => {
    try {
      setSaving(true);
      await platformSettingsService.updateSetting(key, editValue);
      setEditingKey(null);
      setEditValue('');
      setSuccess(`Setting "${key}" updated. Changes take effect within 5 minutes.`);
      await loadSettings();
    } catch (err) {
      // Write-time validation errors arrive as 400 { error } — show the reason, not a generic failure.
      setError(apiErrorMessage(err, `Failed to update "${key}"`));
    } finally {
      setSaving(false);
    }
  };

  // Grouping/sorting only depends on the loaded settings — memoized so per-keystroke
  // re-renders while editing a value don't recompute it for every category.
  // Must stay ABOVE the loading early-return: hooks have to run on every render.
  const groupedCategories = useMemo(() => {
    return Object.keys(settings)
      .sort((a, b) => {
        const ia = CATEGORY_ORDER.indexOf(a);
        const ib = CATEGORY_ORDER.indexOf(b);
        return (
          (ia === -1 ? CATEGORY_ORDER.length : ia) - (ib === -1 ? CATEGORY_ORDER.length : ib) ||
          a.localeCompare(b)
        );
      })
      .map((category) => ({
        category,
        label: getCategoryLabel(category, settings),
        groups: groupRows(category, settings[category]),
        count: settings[category].length,
      }));
  }, [settings]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderTable = (rows: PlatformSettingDto[]) => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Setting</TableCell>
            <TableCell>Value</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Last Modified</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((setting) => (
            <TableRow key={setting.settingKey}>
              <TableCell>
                <Typography variant="body2" fontFamily="monospace">
                  {setting.settingKey.split('.').pop()}
                </Typography>
              </TableCell>
              <TableCell>
                {editingKey === setting.settingKey ? (
                  <TextField
                    size="small"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSave(setting.settingKey);
                      if (e.key === 'Escape') handleCancel();
                    }}
                    autoFocus
                    sx={{ minWidth: 120 }}
                  />
                ) : (
                  <Typography variant="body2" fontFamily="monospace">
                    {setting.settingValue}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {setting.description}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" display="block">
                  {setting.lastModifiedBy}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(setting.lastModifiedAt).toLocaleString()}
                </Typography>
              </TableCell>
              <TableCell align="right">
                {setting.isReadOnly ? (
                  <Tooltip title="Read-only (requires infrastructure change)">
                    <Lock fontSize="small" color="disabled" />
                  </Tooltip>
                ) : editingKey === setting.settingKey ? (
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleSave(setting.settingKey)}
                      disabled={saving || editValue === setting.settingValue}
                    >
                      {saving ? <CircularProgress size={18} /> : <Save fontSize="small" />}
                    </IconButton>
                    <IconButton size="small" onClick={handleCancel}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <IconButton size="small" onClick={() => handleEdit(setting)}>
                    <Edit fontSize="small" />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Platform Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        AI model configuration, rate limits, and processing settings. Changes are cached for up to 5 minutes.
      </Typography>

      {/*
        A degraded startup is not dismissible. It means settings on this very page may be absent
        or stale, so an admin reading a value here could be reading a code default that no one
        chose. This exact state went unnoticed for two days when its only record was a log line.
      */}
      {startupHealth && !startupHealth.isHealthy && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Startup did not complete cleanly — settings below may be incomplete</AlertTitle>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {startupHealth.degradations.map((degradation) => (
              <li key={degradation}>{degradation}</li>
            ))}
          </Box>
          Restart the API after correcting the cause; these steps only run at startup.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {groupedCategories.map(({ category, label, groups, count }) => (
        <Accordion key={category} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">{label}</Typography>
            <Chip
              label={`${count} settings`}
              size="small"
              sx={{ ml: 2, alignSelf: 'center' }}
            />
          </AccordionSummary>
          <AccordionDetails>
            {CATEGORY_INFO[category] && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {CATEGORY_INFO[category]}
              </Alert>
            )}
            {groups.map((group) => (
              <Box key={group.label ?? 'all'} sx={{ mb: 2 }}>
                {group.label && (
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {group.label}
                  </Typography>
                )}
                {renderTable(group.rows)}
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};
